import { catchError, tap, map } from 'rxjs/operators';
import { throwError as rxThrow, Observable } from 'rxjs';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import countBy from 'lodash/countBy';
import compact from 'lodash/compact';
import sortBy from 'lodash/sortBy';

import { Injectable, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { ServerApi } from './server-api';
import { Session } from './session';
import { BulkRequests, ServiceWorkerDynamicConfigService } from './dynamic-configs/bulk-service-worker-config';
import { GroupSelectionImprovementsConfigService } from './dynamic-configs/fe-group-selection-improvements-config';

import { deleteCacheEntry } from '@app/service-worker/dashboard/dashboard.sw.utils';
import { IMoment, moment } from '../lib/moment';
import { extractResponseBody, kmToMiles } from '../lib/utils';
import { ALL_ACCESSIBLE_GROUP_SCOPE, PERMISSIONS } from './../lib/permissions';

import { AUX_STATUS_TYPE } from '../lib/data/aux-equipments';
import { CACHE_NAME } from '@app/service-worker/bulk/bulk.sw.consts';

export interface IVehicleCount {
  active: number;
  deactivated: number;
  total: number;
}

export interface IVehicle {
  inspectionForm?: {
    id: number;
    title: string;
  };
  year?: string;
  make?: string;
  model?: string;
  odometer?: number;
  engineHours?: number;
  isOemEntity?: boolean;
  existingSchedule?: {
    frequencyType: string;
    frequencyValue: number | string;
    maintenanceName: string;
    maintenanceType: string;
  };
  id?: number;
  name: string;
  value: number;
  number?: string;
  groupIds: number[];
  updatedAt: string;
  status: string;
  metric_units: boolean;
  _sortNumber: string;
  type?: string;
  existingReminders?: {
    [key: string]: IVehicleMaintenanceReminder;
  };
  aux?: {
    auxInput1Status?: AUX_STATUS_TYPE;
    auxInput2Status?: AUX_STATUS_TYPE;
  };
}

export interface IVehicleMaintenanceReminder {
  maintenance_name: string;
  maintenance_type: string;
  frequency_type: string;
  frequency_value: number;
}

export interface IListVehicle {
  list: IVehicle[];
}

@Injectable({ providedIn: 'root' })
export class VehiclesSearch {
  private cachedList = [];
  private cachedOemList = [];
  private lastUpdatedAt = null;
  private cachedServerCall$: Observable<any> = null;
  private isPtoCached: boolean = false;
  private hasBulkCacheBeenRefreshedInternal = false;

  public get hasBulkCacheBeenRefreshed(): boolean {
    return this.hasBulkCacheBeenRefreshedInternal;
  }

  constructor(
    private api: ServerApi,
    private session: Session,
    private serviceWorkerDynamicConfigService: ServiceWorkerDynamicConfigService,
    private groupSelectionImprovementsConfigService: GroupSelectionImprovementsConfigService,
  ) {}

  public getList(
    preventGroupFiltering: boolean = false,
    excludeDeactivated: boolean = false,
    excludeNonEldVehicles: boolean = false,
    maintenanceReminder: boolean = false,
    permissionToCheckGroupVisibility: PERMISSIONS = PERMISSIONS.NONE,
    existingReminders: boolean = false,
    isVehiclePto: boolean = false,
    getAuxVehicles: boolean = false,
    includeOemEntities: boolean = false,
    isTriggeredOnLoad: boolean = false,
  ): Observable<IListVehicle> {
    return this.serverRequest(
      maintenanceReminder,
      isVehiclePto,
      getAuxVehicles,
      excludeDeactivated,
      includeOemEntities,
      isTriggeredOnLoad,
    ).pipe(
      map(
        this.mapList.bind(
          this,
          preventGroupFiltering,
          excludeDeactivated,
          excludeNonEldVehicles,
          permissionToCheckGroupVisibility,
          existingReminders,
        ),
      ),
    );
  }

  public clearBulkCache(): Promise<void> {
    deleteCacheEntry(CACHE_NAME, BulkRequests.VEHICLE).then(() => this.hasBulkCacheBeenRefreshedInternal = true);
    return Promise.resolve();
  }

  public getByIds(ids): Observable<IVehicle[]> {
    return this.serverRequest()
      .pipe(
        map(list => {
          return list.filter(v => includes(ids, v.id));
        }),
      );
  }

  public getByNumber(_number) {
    return this.serverRequest()
      .pipe(
        map(list => {
          return list.find(v => v.number.trim() === _number);
        }),
      );
  }

  public getCount(): Observable<IVehicleCount> {
    return this.serverRequest()
      .pipe(
        map(() => {
          const mergedCachedList = [...this.cachedList, ...this.cachedOemList];
          const count = countBy(mergedCachedList, 'status');
          return {
            active: count.active || 0,
            deactivated: count.deactivated || 0,
            total: mergedCachedList.length,
          };
        }),
      );
  }

  private getParams(isVehiclePto: boolean, getAuxVehicles: boolean, maintenanceReminder: boolean, includeOemEntities: boolean) {
    const params = new Map();
    params.set('group_ids', null); // Prevent group_ids param from being sent
    if (this.lastUpdatedAt) {
      params.set('updated_after', this.lastUpdatedAt.formatToServerUtc());
    }

    if (isVehiclePto.toString() !== this.isPtoCached.toString()) {
      params.delete('updated_after');
      this.cachedList = [];
      this.cachedOemList = [];
    }
    if (isVehiclePto) {
      params.set('with_pto', true);
      this.isPtoCached = true;
    } else this.isPtoCached = false;

    if (getAuxVehicles) {
      params.set('with_aux', true);
      this.isPtoCached = true;
    } else this.isPtoCached = false;

    return params;
  }

  private updateCachedList(res) {
    const newList = res.vehicles;
    const oemVehicles = res.oem_entities;
    const cachedList = this.cachedList;
    const cachedOemList = this.cachedOemList;
    newList?.forEach(v => {
      const vehicle = v.vehicle;
      const index = cachedList.findIndex(_v => _v.id === vehicle.id);
      if (index >= 0) {
        cachedList.splice(index, 1, vehicle);
      } else {
        cachedList.push(vehicle);
      }
    });
    if (oemVehicles) {
      oemVehicles?.forEach(v => {
        const vehicle = {
          ...v.oem_entity,
          number: v.oem_entity?.name,
          isOemEntity: true,
        };
        const index = cachedOemList.findIndex(_v => _v.id === vehicle.id);
        if (index >= 0) {
          cachedOemList.splice(index, 1, vehicle);
        } else {
          cachedOemList.push(vehicle);
        }
      });
    }
    const mergedCachedList = [...cachedList, ...cachedOemList];
    return mergedCachedList;
  }

  private serverRequest(
    maintenanceReminder = false,
    isVehiclePto = false,
    getAuxVehicles = false,
    excludeDeactivated: boolean = false,
    includeOemEntities: boolean = false,
    isTriggeredOnLoad: boolean = false,
  ): Observable<any> {
    const params = this.getParams(isVehiclePto, getAuxVehicles, maintenanceReminder, includeOemEntities);
    let api: string = 'vehicles/bulk';
    if (maintenanceReminder) api = 'vehicles/maintenance/bulk?include_maintenance_reminder=true';
    if (excludeDeactivated) params.set('exclude_deactivated', true);
    if (includeOemEntities) params.set('include_oem', true);
    if (this.cachedServerCall$) {
      return this.cachedServerCall$;
    }
    if (isTriggeredOnLoad) {
      params.set('on_app_load', true);
    }
    return (this.cachedServerCall$ = this.api.get(api, params).pipe(
      tap(() => {
        this.cachedServerCall$ = null;
        // lastUpdatedAt should be assigned on initial load so the next load only fetches updates
        if (this.groupSelectionImprovementsConfigService.setLastUpdatedAtOnLoadRequest() || !isTriggeredOnLoad) {
          this.lastUpdatedAt = moment();
        }
      }),
      map(extractResponseBody),
      map(this.updateCachedList.bind(this)),
      catchError((err: HttpErrorResponse) => {
        this.cachedServerCall$ = null;
        return rxThrow(err);
      }),
    ));
  }

  public mapList(
    preventGroupFiltering,
    excludeDeactivated,
    excludeNonEldVehicles,
    permissionToCheckGroupVisibility: PERMISSIONS,
    existingReminders = false,
    list,
  ): IListVehicle {
    const session = this.session;
    const metricUnits = session.getUserMetricUnits();
    let vehicles = filter(list, v => preventGroupFiltering ||
      session.isInSelectedGroups(v.group_ids, permissionToCheckGroupVisibility || ALL_ACCESSIBLE_GROUP_SCOPE))
      .filter(v => !excludeDeactivated || v.status === 'active')
      .filter(v => !excludeNonEldVehicles || v.eld_device_id)
      .map(vehicle => {
        const deactivated = vehicle.status === 'deactivated' ? $localize`(deactivated)` : '';
        const reminder = vehicle.maintenance_reminder;
        return {
          name: compact([vehicle.number, deactivated]).join(' '),
          value: vehicle.id,
          inspectionForm: vehicle.inspection_form,
          groupIds: vehicle.group_ids || [],
          make: vehicle.make,
          model: vehicle.model,
          year: vehicle.year,
          isOemEntity: vehicle.isOemEntity,
          engineHours: vehicle.engine_hours,
          odometer: (vehicle.odometer && !metricUnits) ? kmToMiles(vehicle.odometer) : vehicle.odometer,
          updatedAt: vehicle.updated_at,
          existingSchedule: (reminder && !existingReminders) ? {
            maintenanceName: reminder.maintenance_name,
            maintenanceType: reminder.maintenance_type,
            frequencyType: reminder.frequency_type,
            frequencyValue: reminder.frequency_value,
          } : null,
          existingReminders: existingReminders ? reminder : null,
          // Helper data for sorting
          _sortNumber: vehicle.number?.toLowerCase(),
          aux: vehicle.aux ? {
            auxInput1Status: vehicle.aux.aux_input_1_status ? vehicle.aux.aux_input_1_status : null,
            auxInput2Status: vehicle.aux.aux_input_2_status ? vehicle.aux.aux_input_2_status : null,
            auxInput3Status: vehicle.aux.aux_input_3_status ? vehicle.aux.aux_input_3_status : null,
          } : null,
        };
      });
    vehicles = sortBy(vehicles, '_sortNumber');
    return { list: vehicles };
  }
}
